import { Clock, AlertTriangle, Ban, Moon, Flame, Building2, CheckCircle2, XCircle } from "lucide-react";
import {
  daysBetween,
  daysOverdue,
  deriveInitials,
  isAtRisk,
  isNextActionDue,
  isOverdueInStage,
  priorityScore,
  priorityLabel,
  progressOf,
} from "@/lib/pipeline";
import { OverdueSiren } from "@/components/OverdueSiren";
import Avatar from "@/components/Avatar";
import { KeywordChips } from "@/components/board/ResumeKeywords";
import { EmailedChip } from "@/components/EmailedBadge";

export default function KanbanCard({
  card,
  template,
  users,
  onOpen,
  onDragStart,
  selectable = false,
  selected = false,
  onToggleSelect,
  idSuffix = "",
}) {
  const owner = users.find((u) => u.id === card.owner_id);
  const overdue = isOverdueInStage(card, template);
  const daysOver = daysOverdue(card, template);
  const atRisk = !overdue && isAtRisk(card, template);
  const nextDue = isNextActionDue(card);
  const days = Math.floor(daysBetween(card.stage_entered_at));
  const { done, total, pct } = progressOf(card);
  const snoozed = !!(card.snoozed_until && card.snoozed_until > new Date().toISOString().slice(0, 10));
  const wakeShort = snoozed ? (() => { const [, m, d] = card.snoozed_until.split("-"); return `${m}/${d}`; })() : "";
  const score = snoozed ? 0 : priorityScore(card, template);
  const prio = priorityLabel(score);
  const showPriority = !snoozed && prio.key !== "low";
  const isFinalStage = card.current_stage === 16 || card.current_stage === 109;
  const needsNextStep = !snoozed && !card.next_action_date && !isFinalStage;
  const formatDob = (s) => {
    if (!s) return "";
    const [y, m, d] = s.split("-");
    return m && d && y ? `${m}/${d}/${y}` : s;
  };
  const formatDate = (s) => {
    if (!s) return "";
    const d = new Date(s);
    return isNaN(d) ? "" : `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };

  return (
    <div
      data-testid={`kanban-card-${card.av_id}${idSuffix}`}
      draggable={!selectable}
      onDragStart={(e) => onDragStart?.(e, card)}
      onClick={(e) => {
        if (selectable) return;
        if (e.target.closest("[data-card-select]")) return;
        onOpen(card);
      }}
      className={`bg-white rounded-md shadow-[0_1px_2px_rgba(15,23,42,0.06)] hover:shadow-[0_4px_12px_rgba(15,23,42,0.10)] transition-all cursor-pointer overflow-hidden relative ring-1 ${
        selected ? "ring-2 ring-[#FBBF24]" : "ring-[#15233B]/8 hover:ring-[#15233B]/15"
      } ${snoozed ? "opacity-70" : ""}`}
    >
      {(card.blocked || overdue || atRisk || nextDue || snoozed || showPriority || needsNextStep) && (
        <div className="flex gap-1 px-3 pt-2.5 -mb-1 flex-wrap">
          {card.blocked && (
            <span className="text-[9px] uppercase tracking-[0.15em] font-extrabold px-1.5 py-0.5 rounded inline-flex items-center gap-1 bg-[#7e22ce] text-white">
              <Ban className="h-2.5 w-2.5" /> Blocked
            </span>
          )}
          {snoozed ? (
            <span
              data-testid={`card-snoozed-${card.av_id}`}
              className="text-[9px] uppercase tracking-[0.15em] font-extrabold px-1.5 py-0.5 rounded inline-flex items-center gap-1 bg-[#4F46E5] text-white"
            >
              <Moon className="h-2.5 w-2.5" /> Snoozed · {wakeShort}
            </span>
          ) : (
            <>
              {overdue && (
                <span className="text-[9px] uppercase tracking-[0.15em] font-extrabold px-1.5 py-0.5 rounded inline-flex items-center gap-1 bg-[#b91c1c] text-white">
                  <AlertTriangle className="h-2.5 w-2.5" /> Overdue
                </span>
              )}
              <OverdueSiren daysOver={daysOver} className="text-sm" />
              {atRisk && (
                <span className="text-[9px] uppercase tracking-[0.15em] font-extrabold px-1.5 py-0.5 rounded inline-flex items-center gap-1 bg-[#EAB308] text-[#15233B]">
                  <AlertTriangle className="h-2.5 w-2.5" /> At Risk
                </span>
              )}
              {nextDue && (
                <span className="text-[9px] uppercase tracking-[0.15em] font-extrabold px-1.5 py-0.5 rounded inline-flex items-center gap-1 bg-[#FBBF24] text-white">
                  <Clock className="h-2.5 w-2.5" /> Due
                </span>
              )}
              {showPriority && (
                <span
                  data-testid={`card-priority-${card.av_id}`}
                  className="text-[9px] uppercase tracking-[0.15em] font-extrabold px-1.5 py-0.5 rounded inline-flex items-center gap-1 text-white"
                  style={{ backgroundColor: prio.color }}
                  title={`Priority score ${score}`}
                >
                  <Flame className="h-2.5 w-2.5" /> {prio.label}
                </span>
              )}
              {needsNextStep && (
                <span
                  data-testid={`card-no-next-step-${card.av_id}`}
                  className="text-[9px] uppercase tracking-[0.15em] font-extrabold px-1.5 py-0.5 rounded inline-flex items-center gap-1 bg-[#fde68a] text-[#92400e]"
                >
                  <AlertTriangle className="h-2.5 w-2.5" /> No next step
                </span>
              )}
            </>
          )}
        </div>
      )}
      {selectable && (
        <label
          data-card-select
          onClick={(e) => e.stopPropagation()}
          className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-white border border-[#15233B]/15 rounded px-1 py-0.5 cursor-pointer"
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect?.(card)}
            data-testid={`card-select-${card.av_id}${idSuffix}`}
            className="h-3.5 w-3.5 rounded border-[#15233B]/30 text-[#FBBF24] focus:ring-[#FBBF24]"
          />
        </label>
      )}
      <div className="p-3 flex flex-col gap-2.5">
        <div>
          <div className="text-[14px] font-bold tracking-tight text-[#15233B] leading-snug truncate" title={card.full_name}>
            {card.full_name}
          </div>
          <div className="flex items-center gap-2 text-[10px] mt-0.5 text-[#15233B]/55 font-mono">
            <span className="font-bold">{card.av_id}</span>
            <span>·</span>
            <span className="font-sans uppercase tracking-[0.12em] text-[#15233B]/50">{card.payer_mco}</span>
          </div>
          {card.created_at && (
            <div className="text-[10px] mt-0.5 font-sans text-[#15233B]/55" data-testid={`card-captured-${card.av_id}`}>
              Captured: <span className="font-semibold text-[#15233B]/70">{formatDate(card.created_at)}</span>
            </div>
          )}
          {card.last_contacted_at && (
            <div className="text-[10px] mt-0.5 font-sans text-[#15233B]/55" data-testid={`card-last-contacted-${card.av_id}`}>
              Last contact: <span className="font-semibold text-[#15233B]/70">{formatDate(card.last_contacted_at)}</span>
            </div>
          )}
          {card.last_touch_type && (
            <div className="text-[10px] mt-0.5 font-sans text-[#15233B]/55 truncate" data-testid={`card-touch-${card.av_id}`}>
              Last: <span className="font-semibold text-[#15233B]/70 capitalize">{card.last_touch_type}{card.last_touch_outcome ? ` · ${card.last_touch_outcome.replace(/_/g, " ")}` : ""}</span>
              {card.contact_attempts > 1 ? <span className="ml-1 font-bold text-[#b45309]">· {card.contact_attempts} tries</span> : null}
            </div>
          )}
          {(card.email_count > 0 || card.auto_reply_at) && (
            <div className="mt-1"><EmailedChip card={card} testId={`card-emailed-${card.av_id}`} /></div>
          )}
          {card.dob && (
            <div className="text-[10px] mt-0.5 font-sans text-[#15233B]/55" data-testid={`card-dob-${card.av_id}`}>
              DOB: <span className="font-semibold text-[#15233B]/70">{formatDob(card.dob)}</span>
            </div>
          )}
          {card.current_stage === 16 && card.caregiver && (
            <div
              className="text-[10px] mt-0.5 font-sans uppercase tracking-[0.12em] text-[#15233B]/50 truncate"
              title={`Caregiver: ${card.caregiver}`}
            >
              Caregiver: <span className="text-[#15233B]/65 font-semibold">{card.caregiver}</span>
            </div>
          )}
          {card.keyword_tags?.length > 0 && (
            <div className="mt-1"><KeywordChips tags={card.keyword_tags} max={3} testId={`card-keywords-${card.av_id}`} /></div>
          )}
          {card.current_stage >= 3 && card.current_stage < 100 && (
            <div className="mt-1" data-testid={`card-avida-care-${card.av_id}`}>
              {card.avida_care_sync_status === "sent" ? (
                <span className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded bg-[#166534]/12 text-[#166534]" title={card.avida_care_synced_at ? `Sent to Avida Care ${formatDate(card.avida_care_synced_at)}` : "Sent to Avida Care"}>
                  <CheckCircle2 className="h-2.5 w-2.5" /> Avida Care · Sent
                </span>
              ) : card.avida_care_sync_status === "failed" ? (
                <span className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded bg-[#b91c1c]/12 text-[#b91c1c]" title={card.avida_care_last_error || "Last send failed"}>
                  <XCircle className="h-2.5 w-2.5" /> Avida Care · Failed
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded bg-[#15233B]/8 text-[#15233B]/55" title="Not yet sent to Avida Care">
                  <Building2 className="h-2.5 w-2.5" /> Avida Care · Not sent
                </span>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="flex justify-between text-[10px] font-semibold text-[#15233B]/55 mb-1">
            <span>{done}/{total} tasks</span>
            <span>{days}d in stage</span>
          </div>
          <div className="w-full h-1 bg-[#F1F2F4] rounded-full overflow-hidden">
            <div className="h-full bg-[#15233B] transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <Avatar user={owner} size={20} title={owner?.name} />
          <span className="text-[11px] text-[#15233B]/75 font-semibold truncate">
            {owner?.name || "Unassigned"}
          </span>
          <span className="ml-auto h-5 w-5 rounded-full bg-[#F1F2F4] grid place-items-center text-[9px] font-bold text-[#15233B]/60">
            {deriveInitials(card.full_name)}
          </span>
        </div>
      </div>
    </div>
  );
}
